import { useEffect, useState, useRef, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  X, MapPin, Clock, Truck, User, Navigation, ExternalLink, GripVertical,
  CheckCircle, Circle, ArrowRight, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const DEPOT_POSTCODE = 'B65AD';
const TRAFFIC_COLORS = { green: '#2ecc71', amber: '#f5a623', red: '#e74c3c' };

function LiveClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(iv);
  }, []);
  const day = now.toLocaleDateString('en-GB', { weekday: 'short' }).toUpperCase();
  const time = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-bold" style={{ color: 'var(--amber)', fontFamily: 'var(--fm)' }}>{day}</span>
      <span className="text-lg font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>{time}</span>
    </div>
  );
}

function StopStatusIcon({ status }) {
  if (status === 'completed') return <CheckCircle className="h-5 w-5" style={{ color: 'var(--green)' }} />;
  if (status === 'in_progress') return <Circle className="h-5 w-5 fill-current" style={{ color: 'var(--blue)' }} />;
  return <Circle className="h-5 w-5" style={{ color: 'var(--text-muted)' }} />;
}

function estimateMiles(postcodeA, postcodeB) {
  // Simple UK postcode distance approximation based on area codes
  // In production this would use the Google Distance Matrix API
  if (!postcodeA || !postcodeB) return null;
  const areas = {
    'B': { lat: 52.48, lng: -1.90 }, 'NE': { lat: 54.97, lng: -1.61 },
    'SE': { lat: 51.45, lng: 0.05 }, 'SW': { lat: 51.45, lng: -0.15 },
    'NW': { lat: 53.48, lng: -2.24 }, 'LE': { lat: 52.63, lng: -1.13 },
    'M': { lat: 53.48, lng: -2.24 }, 'L': { lat: 53.40, lng: -2.99 },
    'LS': { lat: 53.80, lng: -1.55 }, 'S': { lat: 53.38, lng: -1.47 },
    'CF': { lat: 51.48, lng: -3.18 }, 'BS': { lat: 51.45, lng: -2.58 },
    'EX': { lat: 50.72, lng: -3.53 }, 'PL': { lat: 50.38, lng: -4.14 },
    'CV': { lat: 52.41, lng: -1.51 }, 'WV': { lat: 52.59, lng: -2.13 },
    'DH': { lat: 54.78, lng: -1.57 }, 'TS': { lat: 54.57, lng: -1.23 },
    'SR': { lat: 54.91, lng: -1.38 }, 'DL': { lat: 54.52, lng: -1.55 },
    'WS': { lat: 52.58, lng: -1.98 }, 'ST': { lat: 53.0, lng: -2.18 },
    'DE': { lat: 52.92, lng: -1.47 }, 'NG': { lat: 52.95, lng: -1.15 },
    'PE': { lat: 52.57, lng: -0.24 }, 'IP': { lat: 52.06, lng: 1.16 },
    'N': { lat: 51.55, lng: -0.10 }, 'E': { lat: 51.53, lng: -0.05 },
    'W': { lat: 51.51, lng: -0.20 }, 'EC': { lat: 51.52, lng: -0.09 },
    'WC': { lat: 51.52, lng: -0.12 },
  };
  const getArea = (pc) => {
    const clean = pc.replace(/\s/g, '').toUpperCase();
    for (const a of Object.keys(areas).sort((a, b) => b.length - a.length)) {
      if (clean.startsWith(a)) return areas[a];
    }
    return areas['B']; // default depot
  };
  const a = getArea(postcodeA);
  const b = getArea(postcodeB);
  const R = 3959; // miles
  const dLat = (b.lat - a.lat) * Math.PI / 180;
  const dLng = (b.lng - a.lng) * Math.PI / 180;
  const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  const straight = R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  return Math.round(straight * 1.3); // 30% road factor
}

function estimateETA(miles) {
  if (!miles) return '--:--';
  const mins = Math.round(miles / 40 * 60); // 40mph average
  const now = new Date();
  now.setMinutes(now.getMinutes() + mins);
  return now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export default function LiveRoutePlanner({ routeKey, routeName, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [traffic, setTraffic] = useState('green');
  const [dragIdx, setDragIdx] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/routeops/planner/route/${routeKey}`);
      setData(res.data);
      setTraffic(res.data.route?.traffic_status || 'green');
    } catch (e) {
      toast.error('Failed to load route planner data');
    } finally {
      setLoading(false);
    }
  }, [routeKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    const iv = setInterval(fetchData, 300000);
    return () => clearInterval(iv);
  }, [fetchData]);

  // Initialize map — Google Maps only (replaced OpenStreetMap).
  // If no API key is configured, the map area shows a configuration prompt.
  useEffect(() => {
    if (!data || !mapRef.current) return;
    const gmKey = data.google_maps_key;

    if (gmKey) {
      // Build an Embed API directions URL
      const depot = 'B6+5AD,Birmingham,UK';
      const dest = data.stops.length > 0 ? encodeURIComponent(data.stops[data.stops.length - 1].postcode) : depot;
      const mid = data.stops.slice(0, -1).map(s => encodeURIComponent(s.postcode)).join('|');
      const embedUrl = `https://www.google.com/maps/embed/v1/directions?key=${gmKey}&origin=${encodeURIComponent(depot)}&destination=${dest}${mid ? `&waypoints=${mid}` : ''}&mode=driving`;

      // Create iframe
      mapRef.current.innerHTML = '';
      const iframe = document.createElement('iframe');
      iframe.src = embedUrl;
      iframe.style.cssText = 'width:100%;height:100%;border:0;';
      iframe.allowFullscreen = true;
      iframe.loading = 'lazy';
      iframe.referrerPolicy = 'no-referrer-when-downgrade';
      iframe.setAttribute('data-testid', 'live-route-gmap-iframe');
      mapRef.current.appendChild(iframe);
      return;
    }

    // No Google Maps key — show a helpful placeholder.
    // Google Maps has replaced OpenStreetMap in the Live Route Planner; the admin
    // must configure a key in RouteOps → Settings and enable BOTH the Geocoding API
    // AND the Maps Embed API in the Google Cloud Console for the iframe to render.
    if (mapRef.current) {
      mapRef.current.innerHTML = `
        <div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;padding:2rem;text-align:center;background:var(--bg-raised);color:var(--text-secondary);font-family:var(--fm);">
          <div style="max-width:480px;">
            <div style="font-size:2.5rem;margin-bottom:0.75rem;">🗺️</div>
            <div style="font-size:14px;font-weight:600;color:var(--text-primary);margin-bottom:0.5rem;">Google Maps API key required</div>
            <div style="font-size:12px;line-height:1.55;">
              The Live Route Planner uses Google Maps.
              Go to <strong style="color:var(--amber);">RouteOps → Settings → Map Providers</strong>,
              paste your key, click <em>Test Connection</em> (checks both Geocoding + Embed APIs),
              then Save.
              <br/><br/>
              <span style="font-size:11px;color:var(--text-muted);">
                In Google Cloud Console, enable: <strong>Geocoding API</strong> &amp; <strong>Maps Embed API</strong>.
              </span>
            </div>
          </div>
        </div>`;
    }
    return;
  }, [data]);

  const handleReorder = async (fromIdx, toIdx) => {
    if (!data) return;
    const newStops = [...data.stops];
    const [moved] = newStops.splice(fromIdx, 1);
    newStops.splice(toIdx, 0, moved);
    const order = newStops.map((s, i) => ({ id: s.id, delivery_order: i + 1 }));
    setData({ ...data, stops: newStops.map((s, i) => ({ ...s, delivery_order: i + 1 })) });
    try {
      await api.put(`/routeops/planner/route/${routeKey}/reorder`, { order });
      toast.success('Delivery order updated');
    } catch { toast.error('Reorder failed'); }
  };

  const handleTrafficChange = async (val) => {
    setTraffic(val);
    try {
      await api.put(`/routeops/planner/route/${routeKey}/traffic`, { traffic: val });
    } catch { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(8,11,16,0.95)' }}>
        <div className="text-center">
          <img src="/flash-runner.gif" alt="Loading" className="h-16 mx-auto mb-3" />
          <p style={{ color: 'var(--text-muted)' }}>Loading route planner...</p>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const activeStop = data.stops.find(s => s.status === 'in_progress') || data.stops.find(s => s.status === 'pending');
  const nextETA = activeStop ? estimateETA(estimateMiles(DEPOT_POSTCODE, activeStop.postcode)) : '--:--';

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: 'var(--bg-void)' }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-3" style={{ background: 'var(--bg-base)', borderBottom: '1px solid var(--border-color)' }}>
        <div>
          <h1 className="text-xl font-bold tracking-wider" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)', letterSpacing: '0.08em' }}>ROUTE PLANNER</h1>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{data.route?.name} — {data.route?.driver_name || 'No driver'}</p>
        </div>
        <div className="flex items-center gap-4">
          <LiveClock />
          <Button variant="ghost" size="sm" onClick={onClose} style={{ color: 'var(--text-muted)' }}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Body: 2x2 grid */}
      <div className="flex-1 grid grid-cols-2 grid-rows-2 gap-3 p-4 overflow-hidden">
        {/* Top Left: Route Status */}
        <div className="rounded-lg p-4 overflow-y-auto" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>Route Status</h2>
          <div className="space-y-1">
            {data.stops.map((s, i) => {
              const prevPc = i === 0 ? DEPOT_POSTCODE : data.stops[i - 1].postcode;
              const miles = estimateMiles(prevPc, s.postcode);
              const eta = estimateETA(miles);
              return (
                <div key={s.id} className="flex items-start gap-3">
                  <div className="flex flex-col items-center">
                    <StopStatusIcon status={s.status} />
                    {i < data.stops.length - 1 && <div className="w-px h-8" style={{ background: 'var(--border-color)' }} />}
                  </div>
                  <div className="flex-1 min-w-0 pb-2">
                    <div className="flex items-center justify-between">
                      <span className={`text-sm font-semibold truncate ${s.status === 'in_progress' ? '' : ''}`}
                        style={{ color: s.status === 'completed' ? 'var(--green)' : s.status === 'in_progress' ? 'var(--blue)' : 'var(--text-primary)' }}>
                        {s.business_name}
                      </span>
                      <Badge variant="outline" className="text-[9px] shrink-0 ml-2"
                        style={{
                          color: s.status === 'completed' ? 'var(--green)' : s.status === 'in_progress' ? 'var(--blue)' : 'var(--text-muted)',
                          borderColor: s.status === 'completed' ? 'var(--green)' : s.status === 'in_progress' ? 'var(--blue)' : 'var(--border-color)',
                        }}>
                        {s.status === 'completed' ? 'Completed' : s.status === 'in_progress' ? 'In Progress' : 'Pending'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-[10px]" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>
                      <span>{s.postcode}</span>
                      {miles && <span>{miles} mi from {i === 0 ? 'depot' : 'prev'}</span>}
                      <span>ETA {eta}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Right: Map + Driver Info */}
        <div className="rounded-lg overflow-hidden flex flex-col" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          {/* Driver header */}
          <div className="flex items-center justify-between px-4 py-2" style={{ borderBottom: '1px solid var(--border-color)' }}>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" style={{ color: 'var(--amber)' }} />
              <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{data.route?.driver_name || 'No driver'}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>ETA next:</span>
              <span className="text-sm font-bold" style={{ color: 'var(--amber)', fontFamily: 'var(--fm)' }}>{nextETA}</span>
              {activeStop && <span className="text-xs truncate max-w-[120px]" style={{ color: 'var(--text-secondary)' }}>{activeStop.business_name}</span>}
            </div>
          </div>
          {/* Map */}
          <div ref={mapRef} className="flex-1" style={{ minHeight: '200px' }} />
        </div>

        {/* Bottom Left: Delivery Order Table */}
        <div className="rounded-lg p-4 overflow-y-auto" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>Delivery Order</h2>
            {data.google_maps_url && (
              <a href={data.google_maps_url} target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs font-medium hover:underline"
                style={{ color: 'var(--amber)' }} data-testid="gmaps-link">
                <Navigation className="h-3.5 w-3.5" /> Google Maps
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
                <th className="text-left py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}></th>
                <th className="text-left py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Business</th>
                <th className="text-left py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Postcode</th>
                <th className="text-center py-2 text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>Order</th>
              </tr>
            </thead>
            <tbody>
              {data.stops.map((s, i) => (
                <tr
                  key={s.id}
                  draggable
                  onDragStart={() => setDragIdx(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => { if (dragIdx !== null && dragIdx !== i) handleReorder(dragIdx, i); setDragIdx(null); }}
                  className="cursor-grab active:cursor-grabbing"
                  style={{ borderBottom: '1px solid var(--border-color)', opacity: dragIdx === i ? 0.5 : 1 }}
                >
                  <td className="py-2 pr-1"><GripVertical className="h-3.5 w-3.5" style={{ color: 'var(--text-muted)' }} /></td>
                  <td className="py-2 font-medium" style={{ color: s.status === 'completed' ? 'var(--green)' : 'var(--text-primary)' }}>{s.business_name}</td>
                  <td className="py-2" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)', fontSize: '11px' }}>{s.postcode}</td>
                  <td className="py-2 text-center">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold"
                      style={{ background: 'var(--amber-glow)', color: 'var(--amber)', fontFamily: 'var(--fm)' }}>
                      {s.delivery_order || i + 1}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Bottom Right: Traffic + Info */}
        <div className="rounded-lg p-4 flex flex-col gap-4" style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)' }}>
          {/* Traffic */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>Traffic Status</h2>
            <div className="flex items-center gap-2">
              {['green', 'amber', 'red'].map(t => (
                <button
                  key={t}
                  onClick={() => handleTrafficChange(t)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all text-sm font-semibold capitalize"
                  style={{
                    borderColor: traffic === t ? TRAFFIC_COLORS[t] : 'var(--border-color)',
                    background: traffic === t ? `${TRAFFIC_COLORS[t]}20` : 'var(--bg-raised)',
                    color: traffic === t ? TRAFFIC_COLORS[t] : 'var(--text-muted)',
                  }}
                  data-testid={`traffic-${t}`}
                >
                  <span className="w-3 h-3 rounded-full" style={{ background: TRAFFIC_COLORS[t] }} />
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* Route summary */}
          <div>
            <h2 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>Route Summary</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-raised)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fm)' }}>{data.stops.length}</div>
                <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Total Stops</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-raised)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--green)', fontFamily: 'var(--fm)' }}>{data.stops.filter(s => s.status === 'completed').length}</div>
                <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Completed</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-raised)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--blue)', fontFamily: 'var(--fm)' }}>{data.stops.filter(s => s.status === 'in_progress').length}</div>
                <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>In Progress</div>
              </div>
              <div className="p-3 rounded-lg" style={{ background: 'var(--bg-raised)' }}>
                <div className="text-2xl font-bold" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>{data.stops.filter(s => s.status === 'pending').length}</div>
                <div className="text-[10px] uppercase" style={{ color: 'var(--text-muted)' }}>Pending</div>
              </div>
            </div>
          </div>

          {/* Vehicle info */}
          <div className="mt-auto">
            <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              <Truck className="h-4 w-4" />
              <span>{data.route?.vehicle_type || '—'} — {data.route?.vehicle_reg || '—'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
              <MapPin className="h-4 w-4" />
              <span>Depot: {DEPOT_POSTCODE}, Birmingham</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
